import { useState } from 'react'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import Link from '@mui/material/Link'
import { Icon } from '@iconify/react'

const CONTACT_EMAIL = 'onare@onadev.net'

// {{CONTACT_EMAIL_DELIVERY}}
// To add server-side email delivery in the future (this is a static export — no API routes):
// Option A: Formspree — create a form at formspree.io and post to their endpoint
// Option B: Web3Forms — free, no backend; post to https://api.web3forms.com/submit
// Option C: EmailJS — client-side only, credentials in env vars (never commit real keys)
// Currently: submits to Web3Forms using access key from env var NEXT_PUBLIC_WEB3FORMS_KEY
// If not configured, falls back to a mailto: link.

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' })
  const [errors, setErrors] = useState({})
  const [status, setStatus] = useState(null) // 'success' | 'error' | 'sending' | 'mailto'

  const validate = () => {
    const newErrors = {}
    if (!form.name.trim()) newErrors.name = 'Name is required'
    if (!form.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Enter a valid email address'
    }
    if (!form.subject.trim()) newErrors.subject = 'Subject is required'
    if (!form.message.trim()) newErrors.message = 'Message is required'

    return newErrors
  }

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value })
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: undefined })
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()
    const validationErrors = validate()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)

      return
    }

    const web3key = process.env.NEXT_PUBLIC_WEB3FORMS_KEY
    if (!web3key) {
      /*
        Sin NEXT_PUBLIC_WEB3FORMS_KEY no hay a donde enviar, asi que se cae a mailto:.
        El problema es que un mailto: sin cliente de correo configurado no hace NADA
        visible: el navegador ignora la navegacion, no hay error, no hay request. El
        usuario ve el formulario lleno y cree que se envio. Probado en Chromium: cero
        navegaciones, cero peticiones, cero feedback.

        Asi que el aviso se muestra SIEMPRE y antes de intentar el mailto. Si el
        cliente de correo abre, el usuario ya tiene lo que necesita; si no abre, tiene
        la direccion a la vista en lugar de un silencio.
      */
      setStatus('mailto')

      const mailto = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(form.subject)}&body=${encodeURIComponent(
        `Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`
      )}`
      window.location.href = mailto

      return
    }

    // Trampa de bots: un campo oculto que una persona nunca completa. Web3Forms
    // descarta el envio si viene con contenido. Sin esto, un formulario publico sin
    // captcha se llena de spam en dias.
    // .checked, no .value: un checkbox devuelve 'on' aunque este desmarcado, y leer
    // .value bloquearia todos los envios reales.
    if (e.target?.botcheck?.checked) return

    setStatus('sending')
    try {
      /*
        FormData y no JSON, a proposito.

        `Content-Type: application/json` NO esta en la lista segura de CORS, asi que el
        navegador manda antes un OPTIONS de verificacion. Si ese preflight falla, el POST
        nunca sale y el error que ve el usuario dice "CORS", que no ayuda a nadie a
        entender que paso.

        Con FormData el navegador pone multipart/form-data, que si esta en la lista
        segura: no hay preflight, hay una sola peticion. Es ademas el camino que usan los
        ejemplos de Web3Forms con formularios HTML planos, o sea el mas probado.
      */
      const datos = new FormData()
      datos.append('access_key', web3key)
      datos.append('name', form.name)
      datos.append('email', form.email)
      datos.append('message', form.message)

      // El asunto lo escribe quien envia, asi que se prefija: sin esto un spammer decide
      // como se ve tu bandeja de entrada y no se distingue de un mensaje real.
      datos.append('subject', `[WAO Tools] ${form.subject}`)

      // Sin replyto, responder al aviso le contesta a Web3Forms y no a la persona.
      datos.append('replyto', form.email)
      datos.append('from_name', `${form.name} (WAO Tools)`)

      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: datos
      })
      const data = await res.json()
      if (data.success) {
        setStatus('success')
        setForm({ name: '', email: '', subject: '', message: '' })
      } else {
        setStatus('error')
      }
    } catch {
      setStatus('error')
    }
  }

  return (
    <>

      <Grid container spacing={6}>
        <Grid size={{ xs: 12 }}>
          <Card
            sx={{
              padding: '2rem',
              borderRadius: '12px',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
              borderTop: '6px solid',
              borderTopColor: 'primary.main'
            }}
          >
            <Typography variant='h4' component='h1' color='primary' gutterBottom>
              Contact
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant='h6' component='h2' color='primary'>
                    Get in Touch
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Have a bug report, a suggestion for a new tool, or a question about War and Order data? We read
                    every message.
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Icon icon='mdi:email-outline' fontSize={20} />
                    <Link href={`mailto:${CONTACT_EMAIL}`} underline='hover' color='primary'>
                      {CONTACT_EMAIL}
                    </Link>
                  </Box>
                  <Typography variant='body2' color='text.secondary'>
                    Expected response time: <strong>within 3–5 business days</strong>
                  </Typography>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 8 }}>
                {status === 'success' && (
                  <Alert severity='success' sx={{ mb: 2 }}>
                    Message sent! We will get back to you within 3–5 business days.
                  </Alert>
                )}
                {status === 'mailto' && (
                  <Alert severity='info' sx={{ mb: 2 }}>
                    Your email app should have opened with this message ready to send. If nothing happened, your
                    browser has no mail app configured — write to{' '}
                    <Link href={`mailto:${CONTACT_EMAIL}`} underline='always' color='inherit'>
                      {CONTACT_EMAIL}
                    </Link>{' '}
                    directly and paste your message there. We read every message and reply within 3–5 business days.
                  </Alert>
                )}
                {status === 'error' && (
                  <Alert severity='error' sx={{ mb: 2 }}>
                    Something went wrong. Please email us directly at {CONTACT_EMAIL}.
                  </Alert>
                )}

                <Box component='form' onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/*
                    Campo trampa. Oculto para personas y para lectores de pantalla, pero visible
                    para un bot que rellena todo lo que encuentra. Si viene con contenido, el
                    envio se descarta en handleSubmit y Web3Forms tambien lo filtra por su lado.
                  */}
                  <input
                    type='checkbox'
                    name='botcheck'
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    style={{ display: 'none' }}
                  />
                  <TextField
                    name='name'
                    label='Name'
                    value={form.name}
                    onChange={handleChange}
                    error={!!errors.name}
                    helperText={errors.name}
                    fullWidth
                    required
                    size='small'
                  />
                  <TextField
                    name='email'
                    label='Email'
                    type='email'
                    value={form.email}
                    onChange={handleChange}
                    error={!!errors.email}
                    helperText={errors.email}
                    fullWidth
                    required
                    size='small'
                  />
                  <TextField
                    name='subject'
                    label='Subject'
                    value={form.subject}
                    onChange={handleChange}
                    error={!!errors.subject}
                    helperText={errors.subject}
                    fullWidth
                    required
                    size='small'
                  />
                  <TextField
                    name='message'
                    label='Message'
                    value={form.message}
                    onChange={handleChange}
                    error={!!errors.message}
                    helperText={errors.message}
                    fullWidth
                    required
                    multiline
                    rows={5}
                    size='small'
                  />
                  <Box>
                    <Button
                      type='submit'
                      variant='contained'
                      disabled={status === 'sending'}
                      startIcon={<Icon icon='mdi:send' />}
                    >
                      {status === 'sending' ? 'Sending…' : 'Send Message'}
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </>
  )
}

Contact.noLoadingGate = true

export default Contact

